import { captureError } from './sentry';

const DEFAULT_ENDPOINT = process.env.REACT_APP_MONITORING_ENDPOINT || '/api/system/log-client-error';

// Only the error name, HTTP status and error code leave the browser: an
// error's message can hold server text or form values.
const serializeError = (error) => {
  const name = typeof error?.name === 'string' ? error.name : 'Error';
  const status = Number.isInteger(error?.response?.status) ? error.response.status : undefined;
  const rawCode = error?.response?.data?.code ?? error?.code;
  const code = typeof rawCode === 'string' && /^[A-Z0-9_]{1,64}$/.test(rawCode) ? rawCode : undefined;
  const message = [name, status, code].filter((part) => part !== undefined).join(' ');
  return { name, status, code, message };
};

const buildPayload = (error, info) => ({
  error: serializeError(error),
  componentStack: info && info.componentStack ? info.componentStack : undefined,
  url: typeof window !== 'undefined' ? window.location.pathname : undefined,
  timestamp: new Date().toISOString()
});

export const reportFrontendError = (error, info) => {
  try {
    // Don't report errors on public pages (landing, login, register)
    const isPublicPage = typeof window !== 'undefined' && (
      window.location.pathname === '/' || 
      window.location.pathname === '/login' || 
      window.location.pathname === '/register'
    );
    
    if (isPublicPage) {
      // Silently ignore errors on public pages to avoid leaking internal errors
      return;
    }

    // Additive: no-ops unless REACT_APP_SENTRY_DSN is configured.
    captureError(error, info);

    const payload = JSON.stringify(buildPayload(error, info));
    const endpoint = DEFAULT_ENDPOINT;

    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([payload], { type: 'application/json' });
      navigator.sendBeacon(endpoint, blob);
      return;
    }

    if (typeof fetch === 'function') {
      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true
      }).catch(() => {});
    }
  } catch (reportError) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('Failed to report frontend error', reportError?.name);
    }
  }
};
